import React from 'react';
import { motion } from 'framer-motion';

interface Course {
  id: string;
  cover?: string;
  title: string;
}

interface CourseListData {
  courseList: Course[];
  remindMessage?: string;
}

interface ChatCourseListProps {
  data?: CourseListData;
  isLoading?: boolean;
}

const ChatCourseList: React.FC<ChatCourseListProps> = ({ data, isLoading = false }) => {
  if (isLoading || !data) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: 'rgba(240, 242, 247, 0.5)',
        borderRadius: '8px',
        marginTop: '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#1890ff',
            marginRight: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
          </div>
          <span style={{ fontWeight: 'bold', color: '#1890ff' }}>课程列表</span>
        </div>
        
        {/* 加载骨架屏 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: 'white',
              borderRadius: '6px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                marginRight: '12px'
              }} />
              <div style={{
                flex: 1,
                height: '16px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px'
              }} />
            </div>
          ))}
        </div>
        
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
      </div>
    );
  }

  const { courseList, remindMessage } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        padding: '16px',
        backgroundColor: 'rgba(240, 242, 247, 0.5)',
        borderRadius: '8px',
        marginTop: '12px'
      }}
    >
      {/* 标题 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#1890ff',
          marginRight: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
          </svg>
        </div>
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>课程列表</span>
      </div>

      {/* 课程列表 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {courseList.map((course, index) => (
          <motion.div
            key={course.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            whileHover={{
              backgroundColor: 'rgba(24, 144, 255, 0.02)',
              borderColor: 'rgba(24, 144, 255, 0.2)'
            }}
          >
            {/* 课程封面 */}
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: course.cover ? 'transparent' : '#f5f5f5',
              borderRadius: '4px',
              marginRight: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: course.cover ? `url(${course.cover})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}>
              {!course.cover && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#ccc">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
              )}
            </div>
            
            {/* 课程标题 */}
            <div style={{
              flex: 1,
              fontSize: '14px',
              color: '#333',
              fontWeight: '500'
            }}>
              {course.title}
            </div>
            
            {/* 箭头图标 */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#ccc">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
          </motion.div>
        ))}
      </div>

      {/* 提醒消息 */}
      {remindMessage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          style={{
            marginTop: '12px',
            padding: '10px',
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#856404',
            textAlign: 'center'
          }}
        >
          {remindMessage}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ChatCourseList; 